// Bloomi replenishment planning engine.
// Pure functions: everything comes in as plain data, nothing is fetched here.
//
// Model (agreed rules, Oct 2026):
// - Monthly buckets from the inventory as-of date's month through the end of the forecast (max 18 months).
//   The current month's demand is prorated from the as-of date.
// - Two locations per SKU: the warehouse (WeWork) and FBA. Amazon forecast is FBA demand; Direct + Other is warehouse demand.
// - Weeks of supply (WOS) = month-end units ÷ (next 3 months of demand ÷ 13).
// - Stock never projects below zero; demand that can't be covered is "short".
// - Firm supply = open POs/shipments and transfers already in the app (dated arrivals). Anything dated before the
//   as-of date is assumed to already be in the uploaded on-hand (and flagged by the loader).
// - Recommendations:
//     * Transfers warehouse → FBA land at the start of a month and ship 14 days earlier.
//     * Supplier orders land at the start of a month; write-by = landing − (production + transit days).
//       Orders land at the product's default destination (warehouse unless set to FBA).
//     * 2-packs (physical bundles) are never ordered from a supplier: they are built at the warehouse from their
//       component singles (BOM qty each) when a transfer needs them.
// - Simplification: a transfer draws on warehouse stock in the month it lands at FBA (the 2-week ship lead is
//   shown as the ship-by date, not modelled as a separate month).

export const TRANSFER_DAYS = 14
// Split products (Catalog → Default PO destination = Split): each PO goes partly straight from the supplier to FBA,
// the rest to the warehouse. FBA gets enough to last until the next PO lands and still be at its weeks-of-supply
// target (12 weeks, same as every SKU), capped; transfers from the warehouse only if FBA heads below target.
export const FBA_MAX_WEEKS = 26 // Amazon's aged-inventory surcharge starts at 181 days
export const SPLIT_ROUND = 12 // FBA share rounded to cases of 12
export const isSplitProduct = p => p?.default_destination === 'split'
export const DEFAULT_LEAD_DAYS = 84 // 12 weeks — used when a SKU has no supplier or its supplier has no lead time (flagged as assumed)
const WEEKS_IN_3_MONTHS = 13
const MAX_MONTHS = 18

// ── date helpers (all dates as 'YYYY-MM-DD' strings, UTC math) ──────────────
const toDate = s => new Date(`${String(s).slice(0, 10)}T00:00:00Z`)
const iso = d => d.toISOString().slice(0, 10)
export const addDays = (s, n) => { const d = toDate(s); d.setUTCDate(d.getUTCDate() + n); return iso(d) }
const monthStart = s => `${String(s).slice(0, 7)}-01`
const addMonths = (s, n) => { const d = toDate(monthStart(s)); d.setUTCMonth(d.getUTCMonth() + n); return iso(d) }
const daysInMonth = s => { const d = toDate(monthStart(s)); d.setUTCMonth(d.getUTCMonth() + 1); d.setUTCDate(0); return d.getUTCDate() }

export function supplierLeadDays(s) {
  if (!s) return null
  const transitField = { ground: 'lead_time_ground_days', sea: 'lead_time_sea_days', air: 'lead_time_air_days' }[s.default_transit_mode || 'ground']
  const prod = Number(s.lead_time_production_days)
  const transit = Number(s[transitField])
  if (!prod || !transit) return null
  return { total: prod + transit, production: prod, transit }
}

/**
 * @param {object} input
 *   planDate: 'YYYY-MM-DD'                       inventory as-of date (latest snapshot)
 *   products: [{ id, sku, name, product_type, supplier_id, default_destination, target_wos_fba, target_wos_warehouse, cost_price, retail_price }]
 *   suppliers: [{ id, name, lead_time_production_days, lead_time_*_days, default_transit_mode, ... }]
 *   bom: [{ parent_product_id, component_product_id, quantity }]
 *   onHand: { [productId]: { wh: n, fba: n } }
 *   forecast: [{ product_id, channel: 'amazon'|'direct'|'other', month: 'YYYY-MM-01', units }]
 *   firm: [{ product_id, loc: 'wh'|'fba', date: 'YYYY-MM-DD', qty (+ in / − out), kind, ref }]
 *   options: { warehouseBasis: 'own' | 'all' (what warehouse WOS is measured against), recommend: false for firm-only,
 *              priority: 'fba' | 'direct' (who gets warehouse stock when it's short; default 'direct') }
 */
export function runPlan({ planDate, products, suppliers, bom, onHand, forecast, firm, options = {} }) {
  const basis = options.warehouseBasis === 'all' ? 'all' : 'own'
  const noRecs = options.recommend === false // "firm supply only" view: no recommended orders or transfers
  // Who gets warehouse stock when there isn't enough for everything in a month:
  //   'fba'    → FBA transfers + 2-pack builds first, Direct/Other sales get what's left
  //   'direct' → Direct/Other sales first, transfers + builds share what's left
  const fbaFirst = options.priority === 'fba'
  const supplierById = Object.fromEntries((suppliers || []).map(s => [s.id, s]))
  const productById = Object.fromEntries(products.map(p => [p.id, p]))

  // ── months ──
  const fcMonths = [...new Set(forecast.map(f => f.month))].sort()
  const firstMonth = monthStart(planDate)
  const lastFc = fcMonths.length ? fcMonths[fcMonths.length - 1] : addMonths(firstMonth, 11)
  const months = []
  for (let m = firstMonth; m <= lastFc && months.length < MAX_MONTHS; m = addMonths(m, 1)) months.push(m)
  if (!months.length) months.push(firstMonth)
  const N = months.length
  const monthIndex = date => {
    const ms = monthStart(date)
    if (ms <= months[0]) return 0
    const i = months.indexOf(ms)
    return i === -1 ? N : i // beyond horizon → N (ignored)
  }
  const dim0 = daysInMonth(planDate)
  const prorate0 = (dim0 - Number(planDate.slice(8, 10)) + 1) / dim0

  // ── bundles (2-packs) ──
  const bundleParts = {} // bundleId -> [{ component, qty }]
  const usedInBundles = {} // singleId -> [{ bundle, qty }]
  ;(bom || []).forEach(b => {
    const parent = productById[b.parent_product_id]
    if (!parent || parent.product_type !== 'physical_bundle' || !productById[b.component_product_id]) return
    ;(bundleParts[parent.id] = bundleParts[parent.id] || []).push({ component: b.component_product_id, qty: Number(b.quantity) || 1 })
    ;(usedInBundles[b.component_product_id] = usedInBundles[b.component_product_id] || []).push({ bundle: parent.id, qty: Number(b.quantity) || 1 })
  })

  // ── demand by product / location / month ──
  const zero = () => new Array(N).fill(0)
  const raw = {} // raw (unprorated) demand, used for next-3-month WOS denominators
  const ensure = id => { raw[id] = raw[id] || { fba: zero(), wh: zero() }; return raw[id] }
  forecast.forEach(f => {
    if (!f.product_id || !productById[f.product_id]) return
    const i = months.indexOf(f.month)
    if (i === -1) return
    ensure(f.product_id)[f.channel === 'amazon' ? 'fba' : 'wh'][i] += Number(f.units) || 0
  })
  const demand = (id, loc, m) => (raw[id]?.[loc]?.[m] || 0) * (m === 0 ? prorate0 : 1)
  const rawAt = (id, loc, m) => {
    const arr = raw[id]?.[loc]
    if (!arr) return 0
    return arr[Math.min(m, N - 1)] || 0 // beyond horizon: carry the last month
  }
  const next3 = (id, loc, m) => rawAt(id, loc, m + 1) + rawAt(id, loc, m + 2) + rawAt(id, loc, m + 3)
  // Warehouse outflow next 3 months for basis 'all': own sales + FBA demand of the SKU + its share of 2-pack FBA demand
  const next3WhAll = (id, m) => {
    let t = next3(id, 'wh', m) + next3(id, 'fba', m)
    ;(usedInBundles[id] || []).forEach(({ bundle, qty }) => { t += qty * (next3(bundle, 'fba', m) + next3(bundle, 'wh', m)) })
    return t
  }
  const wos = (end, n3) => (n3 > 0 ? end / (n3 / WEEKS_IN_3_MONTHS) : null)

  // ── firm supply buckets ──
  const firmB = {}
  ;(firm || []).forEach(f => {
    if (!productById[f.product_id]) return
    const i = monthIndex(f.date < planDate ? planDate : f.date)
    if (i >= N) return
    firmB[f.product_id] = firmB[f.product_id] || { fba: zero(), wh: zero() }
    firmB[f.product_id][f.loc][i] += Number(f.qty) || 0
  })
  const firmAt = (id, loc, m) => firmB[id]?.[loc]?.[m] || 0

  // ── which products to plan ──
  const isBundle = p => p.product_type === 'physical_bundle' && !!bundleParts[p.id]
  const planned = products.filter(p => {
    // Components / raw materials (tubes, bottles, cartons) are bought for cost & cash planning only — they are not
    // sold, so they don't get their own stock plan.
    if (['virtual_bundle', 'component', 'raw_material'].includes(p.product_type) || p.is_active === false) return false
    if (p.product_type === 'physical_bundle' && !bundleParts[p.id]) return false
    const oh = onHand[p.id] || {}
    return raw[p.id] || firmB[p.id] || oh.wh || oh.fba || usedInBundles[p.id]
  })

  // Sunset: every channel the product sells on is set to Sunset in Catalog → keep selling and transferring
  // what's left, but never recommend a new supplier order.
  const isSunset = p => {
    const shopifyLive = p.shopify_enabled !== false && p.shopify_replen_status !== 'sunset'
    const amazonLive = p.amazon_enabled !== false && p.amazon_replen_status !== 'sunset'
    return !shopifyLive && !amazonLive
  }

  // Recommended arrivals land on the 1st of a month: the first month whose 1st is on/after the earliest possible arrival.
  const earliestLanding = date => (date <= planDate ? 0 : monthIndex(date) + (date === monthStart(date) ? 0 : 1))

  // ── per-product timing ──
  const timing = {}
  planned.forEach(p => {
    const sup = supplierById[p.supplier_id]
    const lead = supplierLeadDays(sup)
    const leadDays = lead ? lead.total : DEFAULT_LEAD_DAYS
    const orderLand = addDays(planDate, leadDays)
    const transferLand = addDays(planDate, TRANSFER_DAYS)
    timing[p.id] = {
      supplier: sup || null,
      lead,
      leadDays,
      earliestOrder: isBundle(p) || noRecs || isSunset(p) ? N : earliestLanding(orderLand),
      sunset: isSunset(p),
      earliestOrderDate: orderLand,
      earliestTransfer: noRecs ? N : earliestLanding(transferLand),
      destination: p.default_destination === 'fba' ? 'fba' : 'wh',
      split: isSplitProduct(p) && !isBundle(p),
      fbaTargetWeeks: Number(p.target_wos_fba) || 12,
    }
  })

  // ── state ──
  const S = {}
  planned.forEach(p => {
    const oh = onHand[p.id] || {}
    S[p.id] = {
      fba: { begin: zero(), firmIn: zero(), recIn: zero(), demand: zero(), end: zero(), short: zero(), wos: new Array(N).fill(null), target: zero() },
      wh: { begin: zero(), firmIn: zero(), recIn: zero(), demand: zero(), out: zero(), builds: zero(), buildWant: zero(), end: zero(), short: zero(), wos: new Array(N).fill(null), target: zero() },
      desiredTransfer: zero(), transfer: zero(), buildNeed: zero(), buildActual: zero(),
      orderWh: zero(), orderFba: zero(),
      ohFba: Math.max(0, Number(oh.fba) || 0), ohWh: Math.max(0, Number(oh.wh) || 0),
    }
  })

  const singles = planned.filter(p => !isBundle(p))
  const bundles = planned.filter(isBundle)

  for (let m = 0; m < N; m++) {
    // A. FBA need for every planned SKU
    planned.forEach(p => {
      const s = S[p.id], t = timing[p.id], F = s.fba
      F.begin[m] = m === 0 ? s.ohFba : F.end[m - 1]
      F.firmIn[m] = firmAt(p.id, 'fba', m)
      F.demand[m] = demand(p.id, 'fba', m)
      F.target[m] = t.fbaTargetWeeks * next3(p.id, 'fba', m) / WEEKS_IN_3_MONTHS
      const proj = F.begin[m] + F.firmIn[m] - F.demand[m]
      const need = Math.max(0, Math.ceil(F.target[m] - proj))
      if (t.destination === 'fba' && !isBundle(p)) {
        if (m >= t.earliestOrder) s.orderFba[m] = need
      } else if (m >= t.earliestTransfer) {
        s.desiredTransfer[m] = need
      }
    })

    // B. 2-packs at the warehouse: use any built stock first, the rest must be built from singles
    bundles.forEach(p => {
      const s = S[p.id], W = s.wh
      W.begin[m] = m === 0 ? s.ohWh : W.end[m - 1]
      W.firmIn[m] = firmAt(p.id, 'wh', m)
      W.demand[m] = demand(p.id, 'wh', m)
      W.target[m] = (Number(p.target_wos_warehouse) || 12) * next3(p.id, 'wh', m) / WEEKS_IN_3_MONTHS
      const avail = W.begin[m] + W.firmIn[m]
      s.buildNeed[m] = Math.max(0, Math.ceil(W.demand[m] + s.desiredTransfer[m] + W.target[m] - avail))
    })

    // C. singles at the warehouse: supplier orders, then allocate stock to direct sales → own transfers → 2-pack builds
    singles.forEach(p => {
      const s = S[p.id], t = timing[p.id], W = s.wh
      W.begin[m] = m === 0 ? s.ohWh : W.end[m - 1]
      W.firmIn[m] = firmAt(p.id, 'wh', m)
      W.demand[m] = demand(p.id, 'wh', m)
      const buildDraw = (usedInBundles[p.id] || []).reduce((a, { bundle, qty }) => a + qty * (S[bundle]?.buildNeed[m] || 0), 0)
      const n3 = basis === 'all' ? next3WhAll(p.id, m) : next3(p.id, 'wh', m)
      W.target[m] = (Number(p.target_wos_warehouse) || 12) * n3 / WEEKS_IN_3_MONTHS
      // Next month's FBA transfers ship from this month's ending stock, so hold that pipeline on top of the target
      let pipeline = 0
      if (basis !== 'all' && !t.split && m >= timing[p.id].earliestTransfer - 1) { // split products restock FBA direct, no staging
        pipeline = rawAt(p.id, 'fba', m + 1) + (usedInBundles[p.id] || []).reduce((a, { bundle, qty }) => a + qty * rawAt(bundle, 'fba', m + 1), 0)
        W.target[m] += pipeline
      }
      const proj = W.begin[m] + W.firmIn[m] - W.demand[m] - s.desiredTransfer[m] - buildDraw
      if (m >= t.earliestOrder) s.orderWh[m] = Math.max(0, Math.ceil(W.target[m] - proj))
      W.recIn[m] = s.orderWh[m]
      let avail = W.begin[m] + W.firmIn[m] + W.recIn[m]
      const claims = s.desiredTransfer[m] + buildDraw
      // A transfer landing at the start of month m ships ~2 weeks earlier, so it can only use stock already on the
      // shelf at the start of the month (W.begin) — not supply that arrives during month m.
      const shippable = Math.max(0, W.begin[m])
      let ratio
      if (fbaFirst) {
        ratio = claims > 0 ? Math.min(1, Math.min(avail, shippable) / claims) : 1
        avail -= claims * ratio
        const direct = Math.min(avail, W.demand[m]); avail -= direct
        W.short[m] = W.demand[m] - direct
        avail += claims * ratio // added back: W.out is subtracted below
      } else {
        const direct = Math.min(avail, W.demand[m]); avail -= direct
        W.short[m] = W.demand[m] - direct
        ratio = claims > 0 ? Math.min(1, Math.min(avail, shippable) / claims) : 1
      }
      s.transfer[m] = Math.floor(s.desiredTransfer[m] * ratio)
      const buildGiven = buildDraw * ratio
      ;(usedInBundles[p.id] || []).forEach(({ bundle, qty }) => {
        const b = S[bundle]; if (!b) return
        const want = b.buildNeed[m] * qty
        b._buildFrom = b._buildFrom || {}
        b._buildFrom[p.id] = buildDraw > 0 ? (want / buildDraw) * buildGiven / qty : 0
      })
      W.builds[m] = buildGiven
      W.buildWant[m] = buildDraw
      W.out[m] = s.transfer[m] + buildGiven
      W.end[m] = Math.max(0, avail - W.out[m])
      // WOS excludes the stock staged for next month's FBA transfers (it isn't there for Direct sales)
      W.wos[m] = wos(Math.max(0, W.end[m] - pipeline), basis === 'all' ? next3WhAll(p.id, m) : next3(p.id, 'wh', m))
    })

    // D. finalize 2-packs: builds limited by the scarcest component, then transfer out
    bundles.forEach(p => {
      const s = S[p.id], W = s.wh
      const parts = bundleParts[p.id]
      const built = parts.length ? Math.floor(Math.min(...parts.map(({ component }) => s._buildFrom?.[component] ?? 0))) : 0
      s.buildActual[m] = built
      W.recIn[m] = built
      let avail = W.begin[m] + W.firmIn[m] + built
      if (fbaFirst) {
        s.transfer[m] = Math.min(s.desiredTransfer[m], Math.floor(avail)); avail -= s.transfer[m]
        const direct = Math.min(avail, W.demand[m]); avail -= direct
        W.short[m] = W.demand[m] - direct
        W.end[m] = Math.max(0, avail)
      } else {
        const direct = Math.min(avail, W.demand[m]); avail -= direct
        W.short[m] = W.demand[m] - direct
        s.transfer[m] = Math.min(s.desiredTransfer[m], Math.floor(avail))
        W.end[m] = Math.max(0, avail - s.transfer[m])
      }
      W.out[m] = s.transfer[m]
      W.wos[m] = wos(W.end[m], next3(p.id, 'wh', m))
      s._buildFrom = {}
    })

    // E. finalize FBA
    planned.forEach(p => {
      const s = S[p.id], F = s.fba
      F.recIn[m] = s.transfer[m] + s.orderFba[m]
      const avail = F.begin[m] + F.firmIn[m] + F.recIn[m]
      F.end[m] = Math.max(0, avail - F.demand[m])
      F.short[m] = Math.max(0, F.demand[m] - avail)
      F.wos[m] = wos(F.end[m], next3(p.id, 'fba', m))
    })
  }

  // ── recommendations ──
  const orders = []
  const transfers = []
  planned.forEach(p => {
    const s = S[p.id], t = timing[p.id]
    for (let m = 0; m < N; m++) {
      const land = m === 0 ? planDate : months[m]
      ;[['wh', s.orderWh[m]], ['fba', s.orderFba[m]]].forEach(([dest, qty]) => {
        if (!qty) return
        const writeBy = addDays(land, -t.leadDays) // default 12-week lead time when the supplier's is missing
        orders.push({
          product_id: p.id, sku: p.sku, name: p.name, supplier_id: p.supplier_id || null, supplier_name: t.supplier?.name || null,
          qty, month: months[m], landDate: land, destination: dest, writeBy,
          expedite: !!writeBy && writeBy < planDate,
          needsLeadTime: !t.lead, needsSupplier: !p.supplier_id,
        })
      })
      if (s.transfer[m] > 0 || s.desiredTransfer[m] > 0) {
        const land2 = m === 0 ? addDays(planDate, TRANSFER_DAYS) : months[m]
        const shipBy = addDays(land2, -TRANSFER_DAYS) < planDate ? planDate : addDays(land2, -TRANSFER_DAYS)
        transfers.push({
          product_id: p.id, sku: p.sku, name: p.name, month: months[m], landDate: land2, shipBy,
          qty: s.transfer[m], wanted: s.desiredTransfer[m], shortfall: s.desiredTransfer[m] - s.transfer[m],
          isBundle: isBundle(p), buildQty: isBundle(p) ? s.buildActual[m] : 0,
          components: isBundle(p) ? bundleParts[p.id].map(c => ({ ...c, sku: productById[c.component]?.sku, units: c.qty * s.buildActual[m] })) : [],
        })
      }
    }
  })

  // ── per-SKU summary rows ──
  const rows = planned.map(p => {
    const s = S[p.id]
    const firstShort = loc => { const i = s[loc].short.findIndex(x => x > 0.5); return i === -1 ? null : months[i] }
    const unitsShort = s.fba.short.reduce((a, b) => a + b, 0) + s.wh.short.reduce((a, b) => a + b, 0)
    return {
      product: p, isBundle: isBundle(p), timing: timing[p.id],
      fba: s.fba, wh: s.wh,
      transfer: s.transfer, desiredTransfer: s.desiredTransfer, orderWh: s.orderWh, orderFba: s.orderFba, buildActual: s.buildActual,
      firstShortFba: firstShort('fba'), firstShortWh: firstShort('wh'),
      unitsShort, retailShort: unitsShort * (Number(p.retail_price) || 0),
      hasFbaActivity: s.fba.demand.some(x => x > 0) || s.ohFba > 0 || s.fba.firmIn.some(x => x > 0),
      hasWhActivity: s.wh.demand.some(x => x > 0) || s.ohWh > 0 || s.wh.firmIn.some(x => x > 0) || s.wh.out.some(x => x > 0),
    }
  }).sort((a, b) => a.product.sku.localeCompare(b.product.sku))

  return { months, planDate, prorate0, rows, orders, transfers, basis }
}

/**
 * Cash-out schedule for firm POs and recommended orders.
 * Unit cost: sum of BOM component costs (component cost_price × qty) when the product has a purchasing BOM
 * (finished good → components), otherwise the product's own cost_price. Each component is paid on its own supplier's terms.
 * Payment dates:
 *   deposit: deposit_pct of the line, on the PO date (trigger 'po') or the ship date (trigger 'ship')
 *   balance: the rest, on PO date / ship date / receipt date + net days
 *   ship date = landing date − transit days of the (finished-good) supplier.
 */
export function cashSchedule({ planDate, months, orders, firmPOLines, products, suppliers, bom }) {
  const supplierById = Object.fromEntries((suppliers || []).map(s => [s.id, s]))
  const productById = Object.fromEntries(products.map(p => [p.id, p]))
  const purchaseBom = {}
  ;(bom || []).forEach(b => {
    const parent = productById[b.parent_product_id]
    if (!parent || parent.product_type !== 'finished_good') return
    ;(purchaseBom[parent.id] = purchaseBom[parent.id] || []).push(b)
  })
  const lines = []
  const missing = {}
  const flag = (sku, why) => { missing[sku] = missing[sku] || new Set(); missing[sku].add(why) }

  function payLines({ source, ref, product, qty, orderDate, landDate, unitCostOverride, supplierIdOverride, explodeBom = true }) {
    const parentSup = supplierById[supplierIdOverride || product.supplier_id]
    const lead = supplierLeadDays(parentSup)
    const shipDate = addDays(landDate, -(lead ? lead.transit : 0))
    const parts = explodeBom && purchaseBom[product.id]
      ? purchaseBom[product.id].map(b => ({ item: productById[b.component_product_id], qty: Number(b.quantity) || 1 }))
      : [{ item: product, qty: 1, unitCost: unitCostOverride }]
    parts.forEach(({ item, qty: per, unitCost }) => {
      if (!item) return
      const cost = unitCost ?? Number(item.cost_price)
      const sup = item === product ? parentSup : (supplierById[item.supplier_id] || parentSup)
      if (!cost) { flag(product.sku, `no cost${item !== product ? ` for ${item.sku}` : ''}`); return }
      if (!sup || sup.balance_trigger == null) { flag(product.sku, `no payment terms${sup ? ` for ${sup.name}` : ''}`); return }
      const amount = qty * per * cost
      const depPct = Number(sup.deposit_pct) || 0
      const trig = (k, d) => (k === 'po' ? orderDate : k === 'ship' ? shipDate : landDate)
      if (depPct > 0 && sup.deposit_trigger) {
        lines.push({ source, ref, sku: product.sku, component: item !== product ? item.sku : null, supplier: sup.name, type: 'Deposit', date: trig(sup.deposit_trigger), amount: amount * depPct / 100 })
      }
      lines.push({ source, ref, sku: product.sku, component: item !== product ? item.sku : null, supplier: sup.name, type: 'Balance', date: addDays(trig(sup.balance_trigger), Number(sup.balance_net_days) || 0), amount: amount * (1 - depPct / 100) })
    })
  }

  orders.forEach(o => {
    const p = productById[o.product_id]; if (!p) return
    payLines({ source: 'Recommended', ref: `${o.sku} → ${o.destination === 'fba' ? 'FBA' : 'Warehouse'}`, product: p, qty: o.qty, orderDate: o.writeBy && o.writeBy > planDate ? o.writeBy : planDate, landDate: o.landDate })
  })
  ;(firmPOLines || []).forEach(l => {
    // Fee lines (setup, micro testing, freight…) have no product: paid on the PO supplier's terms
    if (l.fee) {
      const sup = supplierById[l.supplier_id]
      if (!l.amount) return
      if (!sup || sup.balance_trigger == null) { flag(l.po_number, `no payment terms${sup ? ` for ${sup.name}` : ''}`); return }
      const lead = supplierLeadDays(sup)
      const landDate = l.expected_date || planDate
      const orderDate = l.order_date || planDate
      const shipDate = addDays(landDate, -(lead ? lead.transit : 0))
      const trig = k => (k === 'po' ? orderDate : k === 'ship' ? shipDate : landDate)
      const depPct = Number(sup.deposit_pct) || 0
      if (depPct > 0 && sup.deposit_trigger) lines.push({ source: 'Firm PO', ref: l.po_number, sku: l.description || 'Fee', component: null, supplier: sup.name, type: 'Deposit', date: trig(sup.deposit_trigger), amount: l.amount * depPct / 100 })
      lines.push({ source: 'Firm PO', ref: l.po_number, sku: l.description || 'Fee', component: null, supplier: sup.name, type: 'Balance', date: addDays(trig(sup.balance_trigger), Number(sup.balance_net_days) || 0), amount: l.amount * (1 - depPct / 100) })
      return
    }
    const p = productById[l.product_id]; if (!p || !(l.open > 0)) return
    // Cash Out counts POs only (Oct 2026): a firm PO line is paid at its own unit cost. Components are on their own
    // POs, so nothing is costed from a BOM; a line with no unit cost is flagged as missing instead of estimated.
    payLines({ source: 'Firm PO', ref: l.po_number, product: p, qty: l.open, orderDate: l.order_date || planDate, landDate: l.expected_date || planDate, unitCostOverride: Number(l.unit_cost) || 0, supplierIdOverride: l.supplier_id, explodeBom: false })
  })

  // bucket by month; anything dated before the plan month goes in "Past due"
  const first = months[0]
  const byMonth = {}
  lines.forEach(l => {
    const key = l.date < first ? 'past' : l.date.slice(0, 7) + '-01'
    byMonth[key] = (byMonth[key] || 0) + l.amount
  })
  return {
    lines: lines.sort((a, b) => a.date.localeCompare(b.date)),
    byMonth,
    missing: Object.entries(missing).map(([sku, s]) => ({ sku, reasons: [...s] })),
  }
}

/**
 * Plan with supplier order cycles.
 * A supplier PO covers several months (supplier.order_cycle_months, default 3): all of that supplier's monthly
 * needs inside one cycle window are combined into a single order that must be available by the window's first
 * month. Windows are anchored at the supplier's first needed month, so every SKU from that supplier shares the
 * same POs (e.g. Smooth 3oz + 6.7oz on one PR PC Labs PO).
 *   pass 1: monthly needs (runPlan)
 *   pass 2: re-run with the combined orders treated as planned supply → consistent transfers / projections
 */
export const DEFAULT_ORDER_CYCLE_MONTHS = 3

export function planWithOrderCycles(input) {
  const base = { ...input, options: { ...(input.options || {}), recommend: true } }
  const pass1 = runPlan(base)
  const { months, planDate } = pass1
  const supplierById = Object.fromEntries((input.suppliers || []).map(s => [s.id, s]))
  const timingById = Object.fromEntries(pass1.rows.map(r => [r.product.id, r.timing]))
  const cycleOf = sid => Math.max(1, Number(supplierById[sid]?.order_cycle_months) || DEFAULT_ORDER_CYCLE_MONTHS)

  // group monthly needs: supplier (or the SKU itself when it has no supplier)
  const groups = {}
  pass1.orders.forEach(o => {
    const g = o.supplier_id ? `s:${o.supplier_id}` : `p:${o.product_id}`
    ;(groups[g] = groups[g] || []).push({ ...o, idx: months.indexOf(o.month) })
  })
  const combined = {}
  Object.values(groups).forEach(list => {
    const c = cycleOf(list[0].supplier_id)
    const anchor = Math.min(...list.map(o => o.idx))
    list.forEach(o => {
      const w = anchor + Math.floor((o.idx - anchor) / c) * c
      const key = `${o.product_id}|${o.destination}|${w}`
      if (!combined[key]) {
        const t = timingById[o.product_id]
        const land = months[w]
        const writeBy = addDays(land, -(t?.leadDays || DEFAULT_LEAD_DAYS))
        combined[key] = {
          ...o, qty: 0, month: land, landDate: land, writeBy, expedite: writeBy < planDate,
          cycleMonths: c, coversFrom: land, coversTo: months[Math.min(w + c - 1, months.length - 1)],
        }
        delete combined[key].idx
      }
      combined[key].qty += o.qty
    })
  })
  const cycleOrders = Object.values(combined)

  // Split products: open unsplit PO lines (firm entries flagged lotId) + recommended orders are divided FBA / warehouse
  const productById = Object.fromEntries(input.products.map(p => [p.id, p]))
  const lots = [
    ...(input.firm || []).filter(f => f.lotId && isSplitProduct(productById[f.product_id])).map(f => ({ id: f.lotId, product_id: f.product_id, qty: f.qty, date: f.date < planDate ? planDate : f.date })),
    ...cycleOrders.filter(o => isSplitProduct(productById[o.product_id])).map((o, i) => ({ id: `rec:${i}`, order: o, product_id: o.product_id, qty: o.qty, date: o.landDate })),
  ]
  const lotSplits = splitLots({ lots, pass: pass1, products: input.products, cycleOf: pid => cycleOf(productById[pid]?.supplier_id) })
  lots.filter(l => l.order).forEach(l => { const sp = lotSplits[l.id]; l.order.destination = 'split'; l.order.fbaQty = sp.fba; l.order.whQty = sp.wh })
  const firm = applyLotSplits(input.firm || [], lotSplits)

  // pass 2: combined orders become planned supply; anything still missing is added as a top-up
  const planned = cycleOrders.flatMap(o => o.destination === 'split'
    ? [{ product_id: o.product_id, loc: 'fba', qty: o.fbaQty, date: o.landDate, kind: 'planned_order', ref: 'Planner' }, { product_id: o.product_id, loc: 'wh', qty: o.whQty, date: o.landDate, kind: 'planned_order', ref: 'Planner' }]
    : [{ product_id: o.product_id, loc: o.destination, qty: o.qty, date: o.landDate, kind: 'planned_order', ref: 'Planner' }])
  const pass2 = runPlan({ ...base, firm: [...firm, ...planned] })
  const topUps = pass2.orders.map(o => ({ ...o, cycleMonths: 1, coversFrom: o.month, coversTo: o.month, topUp: true }))
  return { ...pass2, orders: [...cycleOrders, ...topUps], monthlyNeeds: pass1.orders, lotSplits, firm }
}

/** Replace flagged open-PO lots (firm entries with lotId) with their FBA / warehouse parts. */
export function applyLotSplits(firm, lotSplits) {
  return firm.flatMap(f => {
    const sp = f.lotId && lotSplits[f.lotId]
    if (!sp) return [f]
    return [{ ...f, loc: 'fba', qty: sp.fba }, { ...f, loc: 'wh', qty: sp.wh }].filter(x => x.qty > 0)
  })
}

/**
 * Decide each split lot's FBA / warehouse split, in arrival order per product, from a plan where the lots all landed
 * at the warehouse (pass 1). Covers the time until the next lot lands (or one order cycle):
 *   warehouse need = Direct/Other sales + singles for 2-pack builds + the warehouse target at the next arrival − stock
 *   FBA need       = Amazon demand + the FBA weeks-of-supply target at the next arrival − FBA stock
 * Enough for both → the warehouse gets its need, FBA gets the rest up to FBA_MAX_WEEKS, any overflow → warehouse.
 * Not enough → FBA first. FBA share is rounded to SPLIT_ROUND.
 */
export function splitLots({ lots, pass, products = [], cycleOf }) {
  const { months, prorate0 } = pass
  const N = months.length
  const rowById = Object.fromEntries(pass.rows.map(r => [r.product.id, r]))
  const productById = Object.fromEntries(products.map(p => [p.id, p]))
  const out = {}
  const byProduct = {}
  lots.forEach(l => { (byProduct[l.product_id] = byProduct[l.product_id] || []).push(l) })
  const pos = date => { // month index + share of that month still ahead on arrival
    const ms = `${date.slice(0, 7)}-01`
    let i = months.indexOf(ms)
    if (i === -1) i = ms < months[0] ? 0 : N + Math.round((Date.parse(ms) - Date.parse(months[N - 1])) / 2.6e9)
    const dim = new Date(Date.UTC(+date.slice(0, 4), +date.slice(5, 7), 0)).getUTCDate()
    const frac = i === 0 ? 1 : (dim - (+date.slice(8, 10)) + 1) / dim // month 0 demand is already prorated
    return { i, frac }
  }
  Object.entries(byProduct).forEach(([pid, list]) => {
    const r = rowById[pid]
    if (!r) { list.forEach(l => { out[l.id] = { fba: 0, wh: l.qty } }); return }
    list.sort((a, b) => a.date.localeCompare(b.date))
    const at = (arr, m) => (m < N ? arr[m] || 0 : arr[N - 1] || 0)
    const unpro = (arr, m) => { const i = Math.min(m, N - 1); const d = arr[i] || 0; return i === 0 && prorate0 > 0 ? d / prorate0 : d }
    // demand per location per month (warehouse = own sales + singles drawn for 2-pack builds)
    const loc = {
      fba: { dem: m => at(r.fba.demand, m), raw: m => unpro(r.fba.demand, m), firm: m => (m < N ? r.fba.firmIn[m] || 0 : 0), start: r.fba.begin[0] || 0, extra: {} },
      wh: { dem: m => at(r.wh.demand, m) + at(r.wh.buildWant, m), raw: m => unpro(r.wh.demand, m), firm: m => (m < N ? r.wh.firmIn[m] || 0 : 0), start: r.wh.begin[0] || 0, extra: {} },
    }
    // pass 1 counted the open lots as warehouse supply — take them back out
    list.filter(l => !l.order).forEach(l => { const a = pos(l.date); if (a.i < N) loc.wh.extra[a.i] = (loc.wh.extra[a.i] || 0) - l.qty })
    const levelAtStart = (L, m) => { let lvl = L.start; for (let i = 0; i < m; i++) lvl = Math.max(0, lvl + L.firm(i) + (L.extra[i] || 0) - L.dem(i)); return lvl }
    const weekly = (L, m) => (L.raw(m) + L.raw(m + 1) + L.raw(m + 2)) / 13
    const whWeeks = Number(productById[pid]?.target_wos_warehouse) || 12
    list.forEach((l, k) => {
      const a = pos(l.date)
      if (a.i >= N) { out[l.id] = { fba: 0, wh: l.qty }; return }
      const next = list[k + 1] ? pos(list[k + 1].date) : { i: a.i + cycleOf(pid), frac: 1 }
      const needOf = (L, endWeeks) => {
        const stock = Math.max(0, levelAtStart(L, a.i) + L.firm(a.i) + (L.extra[a.i] || 0) - L.dem(a.i) * (1 - a.frac))
        let need = L.dem(a.i) * a.frac
        for (let m = a.i + 1; m < next.i; m++) need += L.dem(m)
        if (next.i > a.i) need += L.dem(next.i) * (1 - next.frac)
        else need -= L.dem(a.i) * next.frac
        need += endWeeks * weekly(L, next.i)
        return { stock, need: Math.max(0, need - stock) }
      }
      const f = needOf(loc.fba, Number(productById[pid]?.target_wos_fba) || 12)
      const w = needOf(loc.wh, whWeeks)
      const cap = Math.max(0, FBA_MAX_WEEKS * weekly(loc.fba, a.i) - f.stock)
      let fba = l.qty >= f.need + w.need ? Math.max(f.need, Math.min(l.qty - w.need, cap)) : Math.min(l.qty, f.need)
      fba = Math.min(l.qty, Math.ceil(fba / SPLIT_ROUND) * SPLIT_ROUND)
      out[l.id] = { fba, wh: l.qty - fba }
      loc.fba.extra[a.i] = (loc.fba.extra[a.i] || 0) + fba
      loc.wh.extra[a.i] = (loc.wh.extra[a.i] || 0) + (l.qty - fba)
    })
  })
  return out
}
